import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { useNavigate } from "react-router-dom";
import {
  DollarSign,
  ArrowRightLeft,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Loader2,
  RefreshCw,
  Clock,
  Check,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  HelpCircle,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatCurrency, formatUSDC, formatDate } from "@/lib/utils";
import { companyApi } from "@/services/api";

interface DashboardData {
  balance: {
    usdcBalance: number;
    usdcLockedBalance: number;
  };
}

interface Conversion {
  id: string;
  fromCurrency: string;
  toCurrency: string;
  fromAmount: number;
  toAmount: number;
  rate: number;
  status: string;
  createdAt: string;
  tradeId?: string;
  tradeStatus?: string;
}

// Status config for badges
const TRADE_STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "success" | "destructive" | "outline"; icon: React.ReactNode }> = {
  PENDING: { label: "Pendente", variant: "secondary", icon: <Clock className="w-3 h-3" /> },
  PROCESSING: { label: "Processando", variant: "default", icon: <Loader2 className="w-3 h-3 animate-spin" /> },
  SETTLED: { label: "Liquidado", variant: "outline", icon: <Check className="w-3 h-3" /> },
  COMPLETED: { label: "Concluído", variant: "success", icon: <CheckCircle2 className="w-3 h-3" /> },
  FAILED: { label: "Falhou", variant: "destructive", icon: <XCircle className="w-3 h-3" /> },
  EXPIRED: { label: "Expirado", variant: "secondary", icon: <AlertTriangle className="w-3 h-3" /> },
};

export function CompanyUsdcPage() {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [conversions, setConversions] = useState<Conversion[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashboard, conversionsData] = await Promise.all([
        companyApi.dashboard.get(),
        companyApi.conversion.listConversions(),
      ]);
      setDashboardData(dashboard as DashboardData);
      setConversions(conversionsData);
    } catch (error) {
      console.error("Erro ao carregar dados USDC:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const usdcBalanceCents = dashboardData?.balance?.usdcBalance || 0;
  const usdcBalance = usdcBalanceCents / 100;
  const usdcLockedCents = dashboardData?.balance?.usdcLockedBalance || 0;
  const usdcLocked = usdcLockedCents / 100;
  const usdcAvailable = usdcBalance - usdcLocked;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<DollarSign className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="USDC"
        description="Dólar Digital"
        stats={[
          {
            value: loading ? "..." : formatUSDC(usdcBalance),
            label: "Saldo USDC",
          },
          {
            value: loading ? "..." : formatUSDC(usdcAvailable),
            label: "Disponível",
          },
          ...(usdcLocked > 0
            ? [
                {
                  value: loading ? "..." : formatUSDC(usdcLocked),
                  label: (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-1 cursor-help">
                            Bloqueado
                            <HelpCircle className="w-3 h-3 text-blue-200" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="normal-case max-w-[280px] text-center">
                          Saldo bloqueado por operação de proteção cambial
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ),
                },
              ]
            : []),
        ]}
        actions={
          <Button
            size="sm"
            variant="outline"
            className="bg-white/20 hover:bg-white/30 text-white border-white/30"
            onClick={() => loadData()}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        }
      />

      {/* Card de Saldo */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-blue-500" />
            Saldo USDC
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          ) : (
            <>
              <p className="text-4xl font-bold">{formatUSDC(usdcBalance)}</p>

              {/* Aviso sobre rendimento */}
              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  USDC não possui rendimento. Para rentabilizar, converta para BRLD e aplique.
                </p>
              </div>

              {/* Botões de ação */}
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => navigate("/converter?from=BRLD&to=USDC")}>
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Comprar USDC
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/converter?from=USDC&to=BRLD")}
                  disabled={usdcBalance <= 0}
                >
                  <TrendingDown className="w-4 h-4 mr-2" />
                  Vender USDC
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Histórico de Operações */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Operações USDC</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : conversions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="mb-4">Nenhuma conversão realizada ainda</p>
              <Button variant="outline" onClick={() => navigate("/converter?from=BRLD&to=USDC")}>
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Fazer primeira conversão
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {conversions.slice(0, 10).map((conversion) => {
                const tradeStatusConfig = conversion.tradeStatus ? TRADE_STATUS_CONFIG[conversion.tradeStatus] : null;

                return (
                  <div
                    key={conversion.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {conversion.toCurrency === "USDC" ? (
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-green-600" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                          <TrendingDown className="w-4 h-4 text-red-600" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">
                            {conversion.toCurrency === "USDC" ? "Compra" : "Venda"} USDC
                          </p>
                          {tradeStatusConfig && (
                            <Badge variant={tradeStatusConfig.variant} className="text-[10px] px-1.5 py-0 h-5 flex items-center gap-0.5">
                              {tradeStatusConfig.icon}
                              <span className="hidden sm:inline">{tradeStatusConfig.label}</span>
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(conversion.createdAt)} · Taxa: R$ {conversion.rate.toFixed(4)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {conversion.toCurrency === "USDC" ? (
                        <>
                          <p className="text-sm font-medium text-green-600">
                            +{formatUSDC(conversion.toAmount / 100)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            -{formatCurrency(conversion.fromAmount / 100)}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-green-600">
                            +{formatCurrency(conversion.toAmount / 100)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            -{formatUSDC(conversion.fromAmount / 100)}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card informativo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Sobre o USDC</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">USDC (USD Coin)</strong> é uma stablecoin
            pareada ao dólar americano, emitida pela Circle. Cada USDC é lastreado 1:1 por
            reservas em dólares.
          </p>
          <p>
            No BRLD, você pode converter seu saldo disponível em BRLD para USDC a qualquer
            momento através da tela de <strong className="text-foreground">Converter</strong>.
          </p>
          <p className="text-amber-600">
            Atenção: USDC não possui rendimento. Para rentabilizar seu patrimônio, mantenha
            em BRLD e aplique para ganhar 95% do CDI.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
